// Frontend/src/utils/accountValidation.js

import { 
    createUserWithEmailAndPassword, 
    fetchSignInMethodsForEmail 
  } from "firebase/auth";
  import { 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc,
    collection,
    query,
    where,
    getDocs 
  } from "firebase/firestore";
  import { auth } from "../firebaseConfig"; // Ajustez le chemin selon votre structure
  import { db } from "../firebaseConfig";   // Ajustez le chemin selon votre structure
  
  /**
   * Vérifie si un email existe déjà dans le système
   * @param {string} email - Email à vérifier
   * @returns {Object} Résultat de la vérification
   */
  export const checkEmailExists = async (email) => {
    try {
      // 1. Vérifier dans Firebase Auth
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      if (signInMethods.length > 0) {
        return {
          exists: true,
          inAuth: true,
          message: "Cet email est déjà utilisé dans le système d'authentification."
        };
      }
  
      // 2. Vérifier dans users_webapp
      const userQuery = query(
        collection(db, "users_webapp"),
        where("email", "==", email.toLowerCase())
      );
      const userSnapshot = await getDocs(userQuery);
  
      if (!userSnapshot.empty) {
        const existingUser = userSnapshot.docs[0].data();
        const userId = userSnapshot.docs[0].id;
        
        return {
          exists: true,
          inAuth: false,
          inFirestore: true,
          userData: existingUser,
          userId: userId,
          isApproved: existingUser.isApproved,
          message: existingUser.isApproved 
            ? "Un compte validé avec cet email existe déjà."
            : "Un compte en attente avec cet email existe déjà."
        };
      }
  
      return {
        exists: false,
        message: "Email disponible"
      };
  
    } catch (error) {
      console.error("Erreur vérification email:", error);
      return {
        exists: null,
        error: true,
        message: "Erreur lors de la vérification de l'email"
      };
    }
  };
  
  /**
   * Crée un nouveau compte avec validation complète
   * @param {string} email 
   * @param {string} password 
   * @param {Object} userData 
   * @returns {Object} Résultat de la création
   */
  export const createAccountWithValidation = async (email, password, userData) => {
    try {
      // 1. Vérifier si l'email existe déjà
      const emailCheck = await checkEmailExists(email);
      
      if (emailCheck.exists) {
        return {
          success: false,
          type: emailCheck.isApproved ? "APPROVED_ACCOUNT_EXISTS" : "PENDING_ACCOUNT_EXISTS",
          existingUser: emailCheck.userData,
          userId: emailCheck.userId,
          message: emailCheck.message
        };
      }
  
      if (emailCheck.error) {
        return {
          success: false,
          type: "VALIDATION_ERROR",
          message: emailCheck.message
        };
      }
  
      // 2. Créer le compte Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
  
      // 3. Créer le document dans users_webapp
      await setDoc(doc(db, "users_webapp", user.uid), {
        ...userData,
        email: email.toLowerCase(),
        createdAt: new Date(),
        isApproved: false,
        createdBy: auth.currentUser?.email || "Système",
        uid: user.uid
      });
  
      return {
        success: true,
        message: "Compte créé avec succès ! En attente de validation.",
        userId: user.uid
      };
  
    } catch (error) {
      console.error("Erreur création compte:", error);
      
      // Gestion des erreurs spécifiques Firebase
      switch (error.code) {
        case "auth/email-already-in-use":
          return {
            success: false,
            type: "EMAIL_IN_USE",
            message: "Cet email est déjà utilisé par un autre compte."
          };
        case "auth/weak-password":
          return {
            success: false,
            type: "WEAK_PASSWORD", 
            message: "Le mot de passe doit contenir au moins 6 caractères."
          };
        case "auth/invalid-email":
          return {
            success: false,
            type: "INVALID_EMAIL",
            message: "Format d'email invalide."
          };
        default:
          return {
            success: false,
            type: "UNKNOWN_ERROR",
            message: error.message || "Erreur lors de la création du compte."
          };
      }
    }
  };
  
  /**
   * Réactive un compte en attente
   * @param {string} userId 
   * @param {string} newPassword 
   * @returns {Object} Résultat de la réactivation
   */
  export const reactivatePendingAccount = async (userId, newPassword) => {
    try {
      // 1. Récupérer les données du compte
      const userDoc = await getDoc(doc(db, "users_webapp", userId));
      if (!userDoc.exists()) {
        return {
          success: false,
          message: "Compte introuvable dans la base de données."
        };
      }
  
      const userData = userDoc.data();
      
      // 2. Créer le compte Auth (au cas où il aurait été supprimé)
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          userData.email, 
          newPassword
        );
  
        // 3. Mettre à jour le document avec le nouveau UID si nécessaire
        await updateDoc(doc(db, "users_webapp", userId), {
          updatedAt: new Date(),
          reactivatedBy: auth.currentUser?.email || "Système",
          uid: userCredential.user.uid
        });
  
        return {
          success: true,
          message: "Compte réactivé avec succès !",
          userId: userCredential.user.uid
        };
  
      } catch (authError) {
        if (authError.code === "auth/email-already-in-use") {
          // Le compte Auth existe déjà, juste mettre à jour Firestore
          await updateDoc(doc(db, "users_webapp", userId), {
            updatedAt: new Date(),
            reactivatedBy: auth.currentUser?.email || "Système"
          });
  
          return {
            success: true,
            message: "Compte réactivé avec succès !",
            userId: userId
          };
        } else {
          throw authError;
        }
      }
  
    } catch (error) {
      console.error("Erreur réactivation:", error);
      return {
        success: false,
        message: "Erreur lors de la réactivation du compte: " + error.message
      };
    }
  };
  
  /**
   * Met à jour un compte existant
   * @param {string} userId 
   * @param {Object} newData 
   * @returns {Object} Résultat de la mise à jour
   */
  export const updateExistingAccount = async (userId, newData) => {
    try {
      await updateDoc(doc(db, "users_webapp", userId), {
        ...newData,
        updatedAt: new Date(),
        updatedBy: auth.currentUser?.email || "Système"
      });
  
      return {
        success: true,
        message: "Compte mis à jour avec succès !"
      };
  
    } catch (error) {
      console.error("Erreur mise à jour:", error);
      return {
        success: false,
        message: "Erreur lors de la mise à jour du compte."
      };
    }
  };